import { SubmitHandler } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { Separator } from '@givebutter/primitives/separator';
import { useToast } from '@givebutter/primitives/toast';
import { updateCampaign } from 'api/campaigns';
import * as Form from 'components/form';
import * as SettingsCard from 'components/settings-card';
import * as Wysiwyg from 'components/wysiwyg';
import { UseFormWysiwyg } from 'components/wysiwyg/UseFormWysiwyg';
import { Settings } from 'constants/settings';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { GivebutterAxiosError } from 'types/error';

interface PaymentSettingsValues {
    canAcceptPayments: boolean;
    arePhoneNumbersRequired: boolean;
    isBillingAddressRequired: boolean;
    allowChecks: boolean;
    disableOboCompany: boolean;
    checkMailingInstructions: string;
}

const CampaignToolsPaymentSettings = () => {
    const toast = useToast();
    const { collectCompanyName } = useFlags();
    const { data: campaign } = useCurrentCampaign();
    const methods = Form.useGivebutterForm<PaymentSettingsValues>({
        defaultValues: {
            canAcceptPayments: !campaign?.disablePayments?.active,
            arePhoneNumbersRequired: campaign?.requirePhoneNumbers?.active ?? false,
            isBillingAddressRequired: campaign?.requireBillingAddress?.active ?? false,
            allowChecks: campaign?.checkPaymentInstructions?.active ?? false,
            disableOboCompany: campaign?.disablePaymentOnBehalfOfCompany?.active ?? false,
            checkMailingInstructions: campaign?.checkPaymentInstructions?.value ?? '<p></p>',
        },
    });
    const { watch, control, path, handleSubmit } = methods;
    const [canAcceptPayments, allowChecks] = watch(['canAcceptPayments', 'allowChecks']);

    const { mutate: submit } = useMutation({
        mutationFn: (values: PaymentSettingsValues) => {
            const data = {
                settings: [
                    {
                        code: Settings.DisablePayments.Code,
                        active: !values.canAcceptPayments,
                    },
                    {
                        code: Settings.RequirePhoneNumbers.Code,
                        active: values.arePhoneNumbersRequired,
                    },
                    {
                        code: Settings.RequireBillingAddress.Code,
                        active: values.isBillingAddressRequired,
                    },
                    {
                        code: Settings.DisablePaymentOnBehalfOfCompany.Code,
                        active: values.disableOboCompany,
                    },
                    {
                        code: Settings.CheckPaymentInstructions.Code,
                        active: values.allowChecks,
                        value: values.checkMailingInstructions,
                    },
                ],
            };

            return updateCampaign({ campaignId: campaign?.id!, data });
        },
        onSuccess: () => toast.success('Updated payment settings!'),
        onError: (e: GivebutterAxiosError) =>
            toast.error(e.response?.data?.message ?? 'Failed to update payment settings'),
    });

    return (
        <Form.Provider {...methods}>
            <SettingsCard.Root>
                <SettingsCard.LeftSide>
                    <SettingsCard.Heading>Payment settings</SettingsCard.Heading>
                    <p className={'text-sm text-gray-700'}>
                        Configure the payment options available on your campaign.
                    </p>
                </SettingsCard.LeftSide>
                <SettingsCard.RightSide>
                    <form onSubmit={handleSubmit(submit as SubmitHandler<PaymentSettingsValues>)}>
                        <div className={'flex flex-col gap-4'}>
                            <div className={'flex flex-col gap-2'}>
                                <div className={'flex items-center justify-between gap-2'}>
                                    <Form.Label
                                        className={'text-base'}
                                        name={path('canAcceptPayments')}
                                    >
                                        Accepting payments
                                    </Form.Label>
                                    <Form.Switch control={control} name={'canAcceptPayments'} />
                                </div>
                                <div className={'flex flex-col'}>
                                    <p className={'text-sm text-gray-700'}>
                                        Turn this off if you'd like to close the ability to make
                                        payments but keep your page publicly viewable.
                                    </p>
                                </div>
                            </div>
                            {canAcceptPayments && (
                                <>
                                    <Separator />
                                    <div className={'flex flex-col gap-2'}>
                                        <div className={'flex items-center justify-between gap-2'}>
                                            <Form.Label
                                                className={'text-base'}
                                                name={path('arePhoneNumbersRequired')}
                                            >
                                                Require phone numbers
                                            </Form.Label>
                                            <Form.Switch
                                                control={control}
                                                name={'arePhoneNumbersRequired'}
                                            />
                                        </div>
                                        <div className={'flex flex-col'}>
                                            <p className={'text-sm text-gray-700'}>
                                                Phone numbers are optional at checkout by default.
                                                Turning this on will always require supporters to
                                                enter their phone numbers.
                                            </p>
                                        </div>
                                    </div>
                                    <Separator />
                                    <div className={'flex flex-col gap-2'}>
                                        <div className={'flex items-center justify-between gap-2'}>
                                            <Form.Label
                                                className={'text-base'}
                                                name={path('isBillingAddressRequired')}
                                            >
                                                Require billing address
                                            </Form.Label>
                                            <Form.Switch
                                                control={control}
                                                name={'isBillingAddressRequired'}
                                            />
                                        </div>
                                        <div className={'flex flex-col'}>
                                            <p className={'text-sm text-gray-700'}>
                                                Some payment methods (like PayPal and Venmo) don't
                                                require billing addresses. Turning this on will
                                                require supporters to always enter their billing
                                                address.
                                            </p>
                                        </div>
                                    </div>
                                    <Separator />
                                    {collectCompanyName && (
                                        <>
                                            <div className={'flex flex-col gap-2'}>
                                                <div
                                                    className={
                                                        'flex items-center justify-between gap-2'
                                                    }
                                                >
                                                    <Form.Label
                                                        className={'text-base'}
                                                        name={path('disableOboCompany')}
                                                    >
                                                        Collect company name
                                                    </Form.Label>
                                                    <Form.Switch
                                                        control={control}
                                                        name={'disableOboCompany'}
                                                    />
                                                </div>
                                                <div className={'flex flex-col'}>
                                                    <p className={'text-sm text-gray-700'}>
                                                        When enabled, an optional input field will
                                                        be shown during the checkout process to
                                                        allow supporters to enter a company name.
                                                    </p>
                                                </div>
                                            </div>
                                            <Separator />
                                        </>
                                    )}
                                    <div className={'flex flex-col gap-2'}>
                                        <div className={'flex items-center justify-between gap-2'}>
                                            <Form.Label
                                                className={'text-base'}
                                                name={path('allowChecks')}
                                            >
                                                Allow supporters to select to pay via check
                                            </Form.Label>
                                            <Form.Switch control={control} name={'allowChecks'} />
                                        </div>
                                        <div className={'flex flex-col'}>
                                            <p className={'text-sm text-gray-700'}>
                                                Turning this on will include an additional option
                                                for supporters to mail you a check.
                                            </p>
                                        </div>
                                    </div>
                                    {allowChecks && (
                                        <>
                                            <Separator />
                                            <Form.Container>
                                                <Form.Label
                                                    className={'text-base'}
                                                    name={path('checkMailingInstructions')}
                                                >
                                                    Check mailing instructions
                                                </Form.Label>
                                                <p className={'text-sm text-gray-700'}>
                                                    Add mailing instructions for where a supporter
                                                    should send their check to.
                                                </p>
                                                <Wysiwyg.Root>
                                                    <UseFormWysiwyg
                                                        control={control}
                                                        inline={false}
                                                        name={'checkMailingInstructions'}
                                                        init={{
                                                            menubar: false,
                                                            statusbar: false,
                                                            toolbar: 'bold italic link',
                                                        }}
                                                        rules={{
                                                            validate: value => {
                                                                const trimmed = value
                                                                    .replace(/<[^>]*>/g, '')
                                                                    .replace(/&nbsp;/g, '')
                                                                    .trim();
                                                                if (!trimmed) {
                                                                    return 'Check mailing instructions required';
                                                                }
                                                            },
                                                        }}
                                                    />
                                                </Wysiwyg.Root>
                                                <Form.Error
                                                    name={path('checkMailingInstructions')}
                                                />
                                            </Form.Container>
                                        </>
                                    )}
                                </>
                            )}
                            <div>
                                <Form.SubmitButton>Save</Form.SubmitButton>
                            </div>
                        </div>
                    </form>
                </SettingsCard.RightSide>
            </SettingsCard.Root>
        </Form.Provider>
    );
};

export { CampaignToolsPaymentSettings };
